import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { MAX_DASHBOARD_NAME_LENGTH } from '~/common/constants/max_lengths';
import { trackSegment } from '~/components/SegmentAnalytics';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Text } from '~/eds/src/components/Text/Text';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { SavedDashboardsActions } from '~/enums/SavedDashboards';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { api } from '~/redux/api/createApi';
import { coerceRtkqError } from '~/redux/api/utils';
import { slices } from '~/redux/slices';
import { ERROR, SUCCESS } from '~/types/toast.types';

export const RenameDashboard = () => {
  const intl = useIntl();
  const [name, setName] = useState('');
  const activeModal = useSelector(
    slices.savedDashboards.selectors.selectActiveModal,
  );
  const user = useCurrentUser();

  const [updateDashboard, updateDashboardResult] =
    api.endpoints.updateDashboard.useMutation();
  const { isLoading: isLoadingUpdate } = updateDashboardResult;

  const dispatch = useDispatch();

  const handleSuccess = () => {
    showToast(
      SUCCESS,
      intl.formatMessage({
        id: 'RenameDashboard.successMessage',
        defaultMessage: 'Dashboard renamed successfully.',
        description: 'Success message when dashboard is renamed',
      }),
    );
    dispatch(slices.savedDashboards.actions.closeActiveModal());
  };

  const handleError = (errorUpdate: any) => {
    const resp = coerceRtkqError(errorUpdate)?.response || {};
    const msg = resp.data?.errors?.[0];
    showToast(
      ERROR,
      msg?.title ||
        intl.formatMessage({
          id: 'RenameDashboard.errorMessage',
          defaultMessage: 'An error occurred while renaming the dashboard.',
          description: 'Error message when dashboard rename fails',
        }),
    );
    dispatch(slices.savedDashboards.actions.closeActiveModal());
  };

  useEffect(() => {
    if (activeModal?.selectedDashboard) {
      setName(activeModal.selectedDashboard.name);
    } else {
      setName('');
    }
  }, [activeModal?.selectedDashboard]);

  const getTooltip = () => {
    if (!name?.trim()) {
      return intl.formatMessage({
        id: 'RenameDashboard.enterNameTooltip',
        defaultMessage: 'Please enter a name.',
        description: 'Tooltip when name field is empty',
      });
    } else if (name?.length > MAX_DASHBOARD_NAME_LENGTH) {
      return intl.formatMessage(
        {
          id: 'RenameDashboard.nameTooLongTooltip',
          defaultMessage:
            'A dashboard name can not exceed {maxLength} characters.',
          description:
            'Tooltip when name exceeds maximum length. Example: maxLength = 50',
        },
        { maxLength: MAX_DASHBOARD_NAME_LENGTH },
      );
    } else {
      return '';
    }
  };

  return (
    <Modal
      isVisible={activeModal?.name === SavedDashboardsActions.Rename}
      onHide={() => {
        dispatch(slices.savedDashboards.actions.closeActiveModal());
      }}
      onCancel={() => {
        dispatch(slices.savedDashboards.actions.closeActiveModal());
      }}
      title={intl.formatMessage({
        id: 'RenameDashboard.modalTitle',
        defaultMessage: 'Rename Dashboard',
        description: 'Title of the rename dashboard modal',
      })}
      primaryAction={{
        text: intl.formatMessage({
          id: 'RenameDashboard.saveButton',
          defaultMessage: 'Save Dashboard',
          description: 'Save button text',
        }),
        onClick: async () => {
          trackSegment('submitRenameDashboard', {
            id: activeModal!.selectedDashboard.id,
            name: activeModal!.selectedDashboard.name,
            user_id: user.id,
            client_id: user.client,
          });
          try {
            await updateDashboard({
              uuid: activeModal!.selectedDashboard.id,
              attributes: { name },
            }).unwrap();
            handleSuccess();
          } catch (error) {
            handleError(error);
          }
        },
        isLoading: isLoadingUpdate,
        disabled:
          !name?.trim() ||
          name?.length > MAX_DASHBOARD_NAME_LENGTH ||
          isLoadingUpdate,
        tooltip: getTooltip(),
      }}
    >
      <Text mb={2} variant="body-medium">
        {intl.formatMessage({
          id: 'RenameDashboard.dashboardNameLabel',
          defaultMessage: 'Dashboard Name',
          description: 'Label for dashboard name input field',
        })}
      </Text>
      <TextInput
        autoFocus
        name="dashboardName"
        placeholder={intl.formatMessage({
          id: 'RenameDashboard.namePlaceholder',
          defaultMessage: 'Dashboard name',
          description: 'Placeholder text for dashboard name input',
        })}
        value={name}
        onChange={(value) => setName(value || '')}
      />
    </Modal>
  );
};
